'use client';
import React, { useCallback } from 'react';
import {
  Sidebar,
  SidebarBody,
  SidebarLink,
} from '@/components/ui/sidebar-animated';
import {
  Briefcase,
  CalendarCheck,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  UserCircle,
  Presentation,
  ReceiptPoundSterling,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useSidebarState } from '@/contexts/SidebarContext';
import DashboardToggle from '@/components/dashboard/DashboardToggle';
import { TierBadge } from '@/components/subscription/TierBadge';
import { ThemeToggleInline } from '@/components/ui/theme-toggle';

interface NavigationLink {
  label: string;
  href: string;
  icon: React.ReactNode;
}

// 7-tab navigation (Home, Profile, Jobs, Bookings, Messages, Pitching Room, Invoices)
const NAVIGATION_LINKS: NavigationLink[] = [
  {
    label: 'Calendar',
    href: '/filmmaker/dashboard',
    icon: (
      <LayoutDashboard className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Profile',
    href: '/filmmaker/dashboard/profile',
    icon: (
      <UserCircle className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Jobs Board',
    href: '/filmmaker/dashboard/jobs',
    icon: (
      <Briefcase className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Bookings',
    href: '/filmmaker/dashboard/bookings',
    icon: (
      <CalendarCheck className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Messages',
    href: '/filmmaker/dashboard/messages',
    icon: (
      <MessageSquare className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Pitching Room',
    href: '/filmmaker/dashboard/pitching-room',
    icon: (
      <Presentation className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
  {
    label: 'Invoices',
    href: '/filmmaker/dashboard/invoices',
    icon: (
      <ReceiptPoundSterling className="text-primary dark:text-primary h-5 w-5 flex-shrink-0" />
    ),
  },
];

export function FilmmakerAnimatedSidebar() {
  const location = useLocation();
  const { user, signOut, userType } = useAuth();
  const { isOpen, setIsOpen } = useSidebarState();

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [signOut]);

  // Check if link is active (exact match or nested route)
  const isActiveLink = useCallback(
    (href: string) => {
      // Exact match for dashboard home
      if (href === '/filmmaker/dashboard') {
        return location.pathname === href;
      }
      // Bookings tab also covers applications routes
      if (href === '/filmmaker/dashboard/bookings') {
        return location.pathname.startsWith('/filmmaker/dashboard/bookings') ||
          location.pathname.startsWith('/filmmaker/dashboard/applications');
      }
      // For other routes, check if pathname starts with the href
      return location.pathname.startsWith(href);
    },
    [location.pathname]
  );

  return (
    <>
      <Sidebar open={isOpen} setOpen={setIsOpen}>
      <SidebarBody className="justify-between gap-10 pt-4">
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          {isOpen ? <Logo /> : <LogoIcon />}

          {/* Tier Badge */}
          <div className="mt-6">
            <TierBadge isOpen={isOpen} />
          </div>

          <div className="mt-8 flex flex-col gap-2">
            {NAVIGATION_LINKS.map((link, idx) => {
              const isActive = isActiveLink(link.href);
              return (
                <SidebarLink
                  key={idx}
                  index={idx}
                  link={{
                    label: link.label,
                    href: link.href,
                    icon: link.icon,
                  }}
                  className={cn(
                    isActive
                      ? 'bg-primary/10 dark:bg-primary/20 text-primary font-semibold shadow-sm'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/5',
                    'rounded-xl'
                  )}
                />
              );
            })}
          </div>
        </div>
        <div className="mb-4 space-y-4">
          {/* Dashboard Toggle for "both" users */}
          {userType === 'both' && <DashboardToggle open={isOpen} />}

          {/* Theme Toggle - only show when sidebar is open */}
          {isOpen && (
            <div className="px-2 py-2">
              <ThemeToggleInline />
            </div>
          )}

          <div
            onClick={handleSignOut}
            role="button"
            tabIndex={0}
            className="cursor-pointer"
          >
            <SidebarLink
              link={{
                label: 'Logout',
                href: '#',
                icon: (
                  <div className="flex items-center justify-center h-10 w-10">
                    <LogOut className="text-red-500 h-5 w-5 flex-shrink-0" />
                  </div>
                ),
              }}
              className="text-red-500 px-2 hover:bg-red-500/5 dark:hover:bg-red-500/10 rounded-md"
            />
          </div>
        </div>
      </SidebarBody>
    </Sidebar>
    </>
  );
}

export const Logo = () => {
  const location = useLocation();
  const isFilmmaker = location.pathname.includes('/filmmaker');

  return (
    <Link
      to={isFilmmaker ? '/filmmaker/dashboard' : '/client/dashboard'}
      className="font-normal flex items-center text-sm py-1 relative z-20 group"
    >
      <div className="flex items-center">
        <motion.img
          src="/lovable-uploads/91064b4e-0f45-41a0-8966-0416d131780a.png"
          alt="FindMyFilmmaker Logo"
          className="h-8 w-8 flex-shrink-0 rounded-md object-contain"
          whileHover={{ rotate: 360, scale: 1.1 }}
          transition={{ duration: 0.5, type: "spring" }}
        />
        <motion.span
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          className="font-medium text-black dark:text-white whitespace-pre ml-3"
        >
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            FindMyFilmmaker{' '}
          </motion.span>
          <motion.span 
            className="text-primary"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
          >
            {isFilmmaker ? 'Filmmaker' : 'Client'}
          </motion.span>
        </motion.span>
      </div>
    </Link>
  );
};

export const LogoIcon = () => {
  const location = useLocation();
  const isFilmmaker = location.pathname.includes('/filmmaker');

  return (
    <Link
      to={isFilmmaker ? '/filmmaker/dashboard' : '/client/dashboard'}
      className="font-normal flex justify-center items-center text-sm py-1 relative z-20"
    >
      <motion.img
        src="/lovable-uploads/91064b4e-0f45-41a0-8966-0416d131780a.png"
        alt="FindMyFilmmaker Logo"
        className="h-8 w-8 flex-shrink-0 rounded-md object-contain"
        whileHover={{ rotate: 360, scale: 1.15 }}
        whileTap={{ scale: 0.9 }}
        transition={{ duration: 0.5, type: "spring", stiffness: 300 }}
      />
    </Link>
  );
};